/**
 * 记忆系统 Store
 * 实现智能记忆检索、对话摘要生成、关键词触发机制
 */

import type { Message } from '@xsai/shared-chat'

import type { ConversationSummary, MemoryCategory, MemoryConfig, MemoryDigestJob, MemoryDigestResult, MemoryItem, MemorySearchResult } from '../types/memory'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { MEMORY_DIGEST_TIMEOUT, MEMORY_STORAGE_DEBOUNCE, SUMMARY_GENERATION_INTERVAL } from '../constants/performance'
import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'
import { useContactsStore } from './contacts'
import { useLLM } from './llm'

const DIGEST_PROCESSING_STALE_TIMEOUT = 5 * 60 * 1000

function isStaleProcessingJob(job: MemoryDigestJob, now = Date.now()): boolean {
  return job.status === 'processing'
    && (job.processingAt || job.createdAt) < now - DIGEST_PROCESSING_STALE_TIMEOUT
}

export const useMemoryStore = defineStore('memory', () => {
  const { stream } = useLLM()

  // ============ 状态管理 ============

  /** 对话摘要列表 - ✨ 迁移到 IndexedDB（摘要数据可能很大） */
  const summariesStorage = useIndexedDBStorage<ConversationSummary[]>('memory/summaries', [], {
    debounceMs: MEMORY_STORAGE_DEBOUNCE,
  })
  const summaries = summariesStorage.data

  /** 记忆项列表 - ✨ 迁移到 IndexedDB（记忆数据可能很大） */
  const memoriesStorage = useIndexedDBStorage<MemoryItem[]>('memory/items', [], {
    debounceMs: MEMORY_STORAGE_DEBOUNCE,
  })
  const memories = memoriesStorage.data

  /** 记忆配置 - ✨ 迁移到 IndexedDB（保持一致性） */
  const configStorage = useIndexedDBStorage<MemoryConfig>('memory/config', {
    maxSummaries: 50,
    maxMemories: 100,
    summaryInterval: SUMMARY_GENERATION_INTERVAL,
    summaryRetentionDays: 30, // 保留30天
  })
  const config = configStorage.data

  /** 当前消息计数（按联系人持久化） - ✨ 持久化到 IndexedDB，刷新/切换不丢失 */
  const messageCountMapStorage = useIndexedDBStorage<Record<string, number>>('memory/messageCountMap', {}, {
    debounceMs: 300,
  })
  const messageCountMap = messageCountMapStorage.data

  /** PWA 可恢复的摘要/记忆整理任务。任务先落盘，再发起 LLM 请求。 */
  const digestJobsStorage = useIndexedDBStorage<MemoryDigestJob[]>('memory/digestJobs', [], {
    debounceMs: 300,
  })
  const digestJobs = digestJobsStorage.data
  let digestProcessingPromise: Promise<boolean> | null = null

  // 等待存储加载完成
  const storageReady = Promise.all([
    waitForStorage(summariesStorage),
    waitForStorage(memoriesStorage),
    waitForStorage(configStorage),
    waitForStorage(messageCountMapStorage),
    waitForStorage(digestJobsStorage),
  ]).then(() => {
    console.debug('✅ 记忆系统存储加载完成（IndexedDB）')
  })

  function waitForReady(): Promise<void> {
    return storageReady
  }

  /** 当前激活的联系人ID */
  const currentContactId = ref<string | null>(null)

  /** 当前消息计数（用于判断何时生成摘要） - 从持久化 map 中按联系人读取 */
  const messageCountSinceLastSummary = computed(() => {
    if (!currentContactId.value)
      return 0
    return messageCountMap.value[currentContactId.value] || 0
  })

  // ============ 计算属性 ============

  /** 当前联系人的所有记忆 */
  const currentContactMemories = computed(() =>
    memories.value.filter(m => m.contactId === currentContactId.value),
  )

  /** 当前联系人的所有摘要 */
  const currentContactSummaries = computed(() =>
    summaries.value.filter(s => s.contactId === currentContactId.value),
  )

  /** 核心记忆（重要的记忆永久保留） - 仅当前联系人 */
  const importantMemories = computed(() =>
    currentContactMemories.value.filter(m => m.isImportant),
  )

  /** 普通记忆（按时间排序，最新的在前） - 仅当前联系人 */
  const normalMemories = computed(() =>
    currentContactMemories.value
      .filter(m => !m.isImportant)
      .sort((a, b) => b.timestamp - a.timestamp),
  )

  /** 最近的摘要 - 仅当前联系人 */
  const recentSummaries = computed(() =>
    currentContactSummaries.value
      .sort((a, b) => b.endTime - a.endTime)
      .slice(0, 5),
  )

  const currentContactDigestJobs = computed(() =>
    digestJobs.value
      .filter(j => j.contactId === currentContactId.value && j.status !== 'done')
      .sort((a, b) => a.createdAt - b.createdAt),
  )

  // ============ 核心功能：记忆检索 ============

  function isTrivialRecallInput(input: string): boolean {
    const text = input.trim().toLowerCase()
    if (!text || text.startsWith('/'))
      return true
    return /^(?:[嗯哦好行嗨]|好的|知道了|谢谢|感谢|你好|hi|hello|ok|okay|sure|thanks|continue|go ahead|继续)[\s!！。,.，?？~～]*$/i.test(text)
  }

  function memoryCategoryOf(memory: MemoryItem): MemoryCategory {
    return memory.category || 'fact'
  }

  /**
   * 根据用户输入检索相关记忆
   *
   * 关键词检索：直接字符串包含匹配，零依赖、立即可用。
   * 兜底：结果不足时补充最近的重要记忆。
   *
   * @param userInput 用户输入文本
   */
  function retrieveRelevantMemories(userInput: string, contactId = currentContactId.value): MemorySearchResult {
    const inputLower = userInput.toLowerCase()

    // 收集同角色（cardId）的所有联系人记忆，实现跨人格记忆共享
    const contactsStore = useContactsStore()
    const currentContact = contactId
      ? contactsStore.getContact(contactId)
      : null

    const sharedContactIds = new Set<string>()
    if (contactId)
      sharedContactIds.add(contactId)
    if (currentContact?.cardId) {
      contactsStore.getContactsByCardId(currentContact.cardId)
        .forEach(c => sharedContactIds.add(c.id))
    }

    const sharedSummaries = summaries.value.filter(s =>
      s.contactId && sharedContactIds.has(s.contactId),
    )
    const sharedMemories = memories.value.filter(m =>
      m.contactId
      && sharedContactIds.has(m.contactId)
      && (m.status || 'active') === 'active',
    )

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 摘要部分（保持原有逻辑）
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    // 1. 获取最近2条摘要（无条件包含）
    const recentTwo = sharedSummaries
      .sort((a, b) => b.endTime - a.endTime)
      .slice(0, 2)

    // 2. 基于关键词触发的摘要（直接用 includes 字符串包含）
    const keywordTriggeredSummaries = sharedSummaries.filter(s =>
      s.keywords.some(k => inputLower.includes(k.toLowerCase())),
    )

    // 3. 合并并去重摘要
    const allRelevantSummaries = [...recentTwo, ...keywordTriggeredSummaries]
    const uniqueSummaries = Array.from(
      new Map(allRelevantSummaries.map(s => [s.id, s])).values(),
    )

    // 4. 按时间排序，最多返回4条摘要
    const sortedSummaries = uniqueSummaries
      .sort((a, b) => b.endTime - a.endTime)
      .slice(0, 4)

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 记忆项部分（关键词检索）
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    // 没有语义信号的寒暄不应召回大量陈旧记忆，避免角色突然跳题。
    if (isTrivialRecallInput(userInput)) {
      return {
        summaries: sortedSummaries.slice(0, 1),
        memories: [],
        totalCount: Math.min(1, sortedSummaries.length),
      }
    }

    // 每条记忆的评分表：id -> { memory, score }
    const scoreMap = new Map<string, { memory: MemoryItem, score: number }>()
    const ensureEntry = (m: MemoryItem) => {
      let entry = scoreMap.get(m.id)
      if (!entry) {
        entry = { memory: m, score: 0 }
        scoreMap.set(m.id, entry)
      }
      return entry
    }

    // 关键词检索
    // - 关键词命中（用户输入包含记忆的关键词）得分 1.0
    // - 内容命中（记忆内容包含用户输入）得分 0.8
    for (const m of sharedMemories) {
      const keywordHit = m.keywords.some(k => k && inputLower.includes(k.toLowerCase()))
      const contentHit = !!inputLower && m.content.toLowerCase().includes(inputLower)
      const categoryBonus = ['relationship', 'promise', 'preference'].includes(memoryCategoryOf(m)) ? 0.25 : 0
      const importanceBonus = Math.min(0.25, Math.max(0, (m.importance || (m.isImportant ? 5 : 1)) / 20))
      const ageDays = Math.max(0, (Date.now() - m.timestamp) / (24 * 60 * 60 * 1000))
      const recencyBonus = Math.max(0, 0.2 - ageDays * 0.005)
      if (keywordHit)
        ensureEntry(m).score = 1 + categoryBonus + importanceBonus + recencyBonus
      else if (contentHit)
        ensureEntry(m).score = 0.8 + categoryBonus + importanceBonus + recencyBonus
    }

    const scored = Array.from(scoreMap.values()).map(e => ({
      memory: e.memory,
      finalScore: e.score,
    }))

    // 按最终得分降序；同分时重要记忆优先、再按时间倒序
    scored.sort((a, b) => {
      if (b.finalScore !== a.finalScore)
        return b.finalScore - a.finalScore
      if (a.memory.isImportant !== b.memory.isImportant)
        return a.memory.isImportant ? -1 : 1
      return b.memory.timestamp - a.memory.timestamp
    })

    let finalMemories = scored.map(s => s.memory)

    // 兜底机制：如果匹配少于2条，补充最近的重要记忆
    if (finalMemories.length < 2) {
      const fallback = sharedMemories
        .filter(m => m.isImportant && !finalMemories.some(fm => fm.id === m.id))
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 2 - finalMemories.length)

      finalMemories = [...finalMemories, ...fallback]

      if (fallback.length > 0) {
        console.debug('🛟 兜底补充了', fallback.length, '条重要记忆')
      }
    }

    // 最多返回5条
    const sortedMemories = finalMemories.slice(0, 5)

    if (import.meta.env.DEV && scored.length > 0) {
      console.debug('🔍 记忆检索（关键词）:', scored.slice(0, 5).map(s => ({
        content: s.memory.content.slice(0, 20),
        score: s.finalScore.toFixed(3),
      })))
    }

    return {
      summaries: sortedSummaries,
      memories: sortedMemories,
      totalCount: sortedSummaries.length + sortedMemories.length,
    }
  }

  /**
   * 从文本中提取关键词（纯字符串分词，仅作最后兜底）
   * ⚠️ 质量较低，优先使用 AI 生成的关键词；仅在 AI 未提供时才调用本函数。
   */
  function extractKeywords(text: string): string[] {
    // 中文高频停用词（无意义词不进关键词索引）
    const STOPWORDS = new Set([
      '的',
      '了',
      '在',
      '是',
      '我',
      '有',
      '和',
      '就',
      '不',
      '人',
      '都',
      '一',
      '一个',
      '上',
      '也',
      '很',
      '到',
      '说',
      '要',
      '去',
      '你',
      '会',
      '着',
      '没有',
      '看',
      '好',
      '自己',
      '这',
      '那',
      '但',
      '而',
      '及',
      '与',
      '或',
      '所',
      '为',
      'the',
      'a',
      'an',
      'is',
      'are',
      'was',
      'were',
      'have',
      'has',
      'do',
      'does',
      'will',
      'to',
      'of',
      'in',
      'on',
      'at',
      'by',
      'for',
      'with',
      'it',
      'this',
      'that',
      'and',
      'or',
      'but',
    ])

    const words = text
      .replace(/[^\u4E00-\u9FA5a-z0-9\s]/gi, ' ')
      .split(/\s+/)
      .filter(w => w.length > 1 && !STOPWORDS.has(w.toLowerCase()))

    return [...new Set(words)]
  }

  // ============ 核心功能：生成对话摘要 ============

  function messageText(message: Message): string {
    const simulatedImages = (message as any).simulatedImages
    if (Array.isArray(simulatedImages) && simulatedImages.length > 0) {
      return simulatedImages.map((image: any, index: number) => {
        const description = typeof image?.description === 'string' ? image.description.trim() : ''
        return description ? `图片${index + 1}：${description}` : ''
      }).filter(Boolean).join('\n')
    }
    return typeof message.content === 'string'
      ? message.content
      : (message.content ?? []).map(c => 'text' in c ? c.text : '').join(' ')
  }

  function applyMemoryOperations(result: MemoryDigestResult, jobId?: string, contactId = currentContactId.value) {
    const operations = result.memoryOperations || []
    for (const operation of operations) {
      try {
        const category = operation.category || 'fact'
        const confidence = Math.min(1, Math.max(0, operation.confidence ?? 0.75))
        const importance = Math.min(5, Math.max(0, operation.importance ?? 1))
        const match = operation.match?.trim()
        const existing = memories.value.find(m =>
          m.contactId === contactId
          && (m.status || 'active') === 'active'
          && match
          && (m.content.includes(match) || m.id === match),
        )

        if (operation.action === 'forget') {
          if (confidence < 0.85)
            continue
          if (existing && !existing.isImportant)
            existing.status = 'forgotten'
          continue
        }

        if (operation.action === 'supersede' && !operation.content)
          continue

        if (operation.action !== 'add' && operation.action !== 'supersede')
          continue

        if (!operation.content?.trim())
          continue

        // 自动记忆只保存高信号内容；核心记忆仍由用户手动标记，关系/承诺达到最高等级时例外。
        const accepted = confidence >= 0.75
          && (importance >= 3 || category === 'relationship' || category === 'promise')
        if (!accepted)
          continue

        if (operation.action === 'supersede') {
          if (existing && !existing.isImportant)
            existing.status = 'superseded'
        }

        const isImportant = importance >= 5
          && confidence >= 0.85
          && (category === 'relationship' || category === 'promise')
        createMemory(
          operation.content.trim(),
          operation.keywords || [],
          isImportant,
          operation.emotion,
          {
            category,
            confidence,
            importance,
            source: 'auto_digest',
            sourceJobId: jobId,
            contactId: contactId || undefined,
          },
        )
      }
      catch (error) {
        // 单个异常字段不应让摘要任务失败并在下一轮重复写入整段摘要。
        console.warn('⚠️ 跳过格式异常的自动记忆操作:', error)
      }
    }

    for (const loop of result.openLoops || []) {
      try {
        if (!loop.content?.trim())
          continue
        const confidence = Math.min(1, Math.max(0, loop.confidence ?? 0.8))
        const importance = Math.min(5, Math.max(0, loop.importance ?? 3))
        if (confidence < 0.75 || importance < 3)
          continue
        createMemory(loop.content.trim(), loop.keywords || [], false, undefined, {
          category: 'open_loop',
          confidence,
          importance,
          source: 'auto_digest',
          sourceJobId: jobId,
          contactId: contactId || undefined,
        })
      }
      catch (error) {
        console.warn('⚠️ 跳过格式异常的未完成话题:', error)
      }
    }
  }

  /** 一次调用同时生成摘要、情感记忆、关系记忆和未完成话题。 */
  async function generateDigest(
    messages: Message[],
    model: string,
    provider: any,
    options: { jobId?: string, abortSignal?: AbortSignal, contactId?: string } = {},
  ): Promise<ConversationSummary | null> {
    if (messages.length === 0)
      return null

    // 无激活联系人时拒绝生成，避免产生 contactId 为空的孤儿摘要（与 createMemory 守卫对称）
    const digestContactId = options.contactId || currentContactId.value
    if (!digestContactId) {
      console.warn('[generateSummary] 无激活联系人，跳过以避免产生孤儿摘要')
      return null
    }

    try {
      const conversationText = messages
        .map(m => `[${m.role === 'user' ? '用户' : 'AI'}]: ${messageText(m).slice(0, 1200)}`)
        .join('\n')

      const summaryPrompt = `
你是虚拟伴侣的长期记忆整理器。请使用一次工具调用完成摘要和记忆整理。

对话内容：
${conversationText}

**重要**：
- summary 使用第三人称，记录事实、情绪和关系变化，但不要编造。
- 只记录稳定偏好、重要事件、承诺、关系转折和未完成话题。
- 普通寒暄、一次性情绪和无法确认的猜测不要写入长期记忆。
- 新事实与旧记忆冲突时使用 supersede，并在 match 中写出旧记忆中的独特短语。
- confidence 是 0 到 1；importance 为 0 到 5。
- openLoops 只保留下一次对话值得自然跟进的事项。

importance取值：
- 0-2: 普通对话（日常闲聊）
- 3-4: 重要对话（有价值的信息交流）
- 5: 核心对话（涉及重要承诺、决定、情感转折点）

请调用 submit_conversation_summary 工具提交结果。
`

      // 动态导入工具构建函数（避免循环依赖）
      const { buildConversationSummaryTool } = await import('../tools/chat-actions')
      const summaryTool = buildConversationSummaryTool()

      let summaryResult: MemoryDigestResult | null = null

      await new Promise<void>((resolve, reject) => {
        stream(model, provider, [
          { role: 'user', content: summaryPrompt },
        ], {
          tools: [summaryTool],
          async onStreamEvent(event) {
            if (event.type === 'tool-call') {
              // 捕获工具调用的参数作为摘要结果
              // event.args 在 OpenAI 兼容路径上是 JSON 字符串，原生路径是对象，统一处理
              const rawArgs = typeof event.args === 'string' ? JSON.parse(event.args) : event.args
              const args = rawArgs as MemoryDigestResult
              summaryResult = {
                summary: args.summary,
                keywords: args.keywords || [],
                importance: args.importance || 1,
                memoryOperations: args.memoryOperations || [],
                openLoops: args.openLoops || [],
                emotionalContext: args.emotionalContext,
              }
              console.info('📊 [generateSummary] 收到摘要:', summaryResult)
            }
            else if (event.type === 'finish') {
              resolve()
            }
            else if (event.type === 'error') {
              reject(new Error(event.error))
            }
          },
          abortSignal: options.abortSignal,
        })
      })

      if (!summaryResult) {
        console.warn('❌ [generateSummary] AI 未调用 submit_conversation_summary 工具')
        return null
      }

      const confirmedResult: MemoryDigestResult = summaryResult

      // 优化3：使用消息的实际时间戳，而非粗略估算
      const firstMsgTimestamp = (messages[0] as any)?.timestamp
      const lastMsgTimestamp = (messages[messages.length - 1] as any)?.timestamp
      const startTime = firstMsgTimestamp || (Date.now() - (messages.length * 60000))
      const endTime = lastMsgTimestamp || Date.now()

      const summary: ConversationSummary = {
        id: `summary_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: confirmedResult.summary,
        keywords: confirmedResult.keywords,
        startTime,
        endTime,
        messageCount: messages.length,
        importance: confirmedResult.importance,
        createdAt: Date.now(),
        contactId: digestContactId,
        sourceJobId: options.jobId,
      }

      const existingSummary = options.jobId
        ? summaries.value.find(s => s.sourceJobId === options.jobId)
        : undefined
      if (!existingSummary) {
        addSummary(summary)
      }
      // 即使摘要已经写入，记忆持久化也可能在上一轮失败；重试时再次应用操作，
      // createMemory 的内容去重和状态更新保证不会重复膨胀。
      applyMemoryOperations(confirmedResult, options.jobId, digestContactId)

      return existingSummary || summary
    }
    catch (error) {
      console.error('❌ 生成对话摘要失败:', error)
      return null
    }
  }

  /** 保留旧接口，手动生成摘要和旧调用方继续可用。 */
  async function generateSummary(
    messages: Message[],
    model: string,
    provider: any,
  ): Promise<ConversationSummary | null> {
    return generateDigest(messages, model, provider)
  }

  function toDigestMessages(messages: Message[]): MemoryDigestJob['messages'] {
    return messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({
        role: m.role as 'user' | 'assistant',
        content: messageText(m).slice(0, 1200),
        timestamp: (m as any).timestamp,
      }))
      .filter(m => m.content.trim().length > 0)
  }

  /** 已完成任务只保留少量审计记录，避免快照长期占用 PWA 存储和内存。 */
  function pruneCompletedDigestJobs() {
    const doneByContact = new Map<string, MemoryDigestJob[]>()
    for (const job of digestJobs.value) {
      if (job.status !== 'done')
        continue
      const list = doneByContact.get(job.contactId) || []
      list.push(job)
      doneByContact.set(job.contactId, list)
    }
    const keep = new Set<string>()
    for (const jobs of doneByContact.values()) {
      jobs
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 20)
        .forEach(job => keep.add(job.id))
    }
    const next = digestJobs.value.filter(job => job.status !== 'done' || keep.has(job.id))
    if (next.length !== digestJobs.value.length)
      digestJobs.value = next
  }

  /** 创建一个幂等的整理任务；调用方应在发起 LLM 请求前调用。 */
  async function enqueueDigestJob(messages: Message[], contactId = currentContactId.value): Promise<MemoryDigestJob | null> {
    await waitForReady()
    if (!contactId)
      return null
    const snapshot = toDigestMessages(messages)
    if (snapshot.length < 2)
      return null

    const endTime = snapshot.at(-1)?.timestamp || Date.now()
    const startTime = snapshot[0]?.timestamp || endTime
    const existing = digestJobs.value.find(job =>
      job.contactId === contactId && job.status !== 'done',
    )
    if (existing) {
      // 同一联系人只允许一个未完成任务。等待中的任务可以吸收新消息，
      // 正在处理的任务保持快照不变，避免并发请求和重复写入记忆。
      if (existing.status !== 'processing' && endTime > existing.endTime) {
        existing.messages = snapshot
        existing.messageCount = snapshot.length
        existing.startTime = Math.min(existing.startTime, startTime)
        existing.endTime = endTime
        existing.turnCount = messageCountMap.value[contactId] || existing.turnCount || 0
        existing.status = 'pending'
        existing.attempts = 0
        existing.nextAttemptAt = Date.now()
        existing.lastError = undefined
        await digestJobsStorage.save()
      }
      return existing
    }

    const now = Date.now()
    const job: MemoryDigestJob = {
      id: `digest_${now}_${Math.random().toString(36).slice(2, 10)}`,
      contactId,
      messages: snapshot,
      messageCount: snapshot.length,
      turnCount: messageCountMap.value[contactId] || 0,
      startTime,
      endTime,
      status: 'pending',
      attempts: 0,
      nextAttemptAt: now,
      createdAt: now,
    }
    digestJobs.value.push(job)
    pruneCompletedDigestJobs()
    // 任务必须先落盘，避免 PWA 被系统回收时丢失这次整理请求。
    await digestJobsStorage.save()
    return job
  }

  function recoverStaleDigestJobs() {
    const now = Date.now()
    let changed = false
    for (const job of digestJobs.value) {
      if (isStaleProcessingJob(job, now)) {
        job.status = 'pending'
        job.nextAttemptAt = now
        job.processingAt = undefined
        changed = true
      }
    }
    if (changed)
      digestJobsStorage.notifyChange()
  }

  /** 在 PWA 前台处理一个最早的待办任务；用户聊天请求由调用方负责优先。 */
  async function processNextDigestJob(model: string, provider: any, canProcess?: () => boolean): Promise<boolean> {
    await waitForReady()
    if (!currentContactId.value || (typeof document !== 'undefined' && document.visibilityState !== 'visible'))
      return false
    if (canProcess && !canProcess())
      return false
    recoverStaleDigestJobs()
    const job = digestJobs.value.find(j =>
      j.contactId === currentContactId.value
      && (j.status === 'pending' || j.status === 'failed')
      && j.nextAttemptAt <= Date.now(),
    )
    if (!job)
      return false

    job.status = 'processing'
    job.attempts += 1
    job.processingAt = Date.now()
    job.lastError = undefined
    digestJobsStorage.notifyChange()
    // processing 状态也立即持久化；刷新后可以识别并恢复中断任务。
    await digestJobsStorage.save()

    if (canProcess && !canProcess()) {
      job.status = 'pending'
      job.nextAttemptAt = Date.now()
      job.processingAt = undefined
      await digestJobsStorage.save()
      return false
    }

    const abortController = new AbortController()
    const timeoutId = setTimeout(() => abortController.abort(), MEMORY_DIGEST_TIMEOUT)
    let countReset = false
    const messageCountBefore = messageCountMap.value[job.contactId] || 0
    try {
      const summary = await generateDigest(job.messages as Message[], model, provider, {
        jobId: job.id,
        abortSignal: abortController.signal,
        contactId: job.contactId,
      })
      if (!summary)
        throw new Error('整理请求未返回有效摘要')
      summariesStorage.error.value = null
      memoriesStorage.error.value = null
      messageCountMapStorage.error.value = null
      await Promise.all([
        summariesStorage.save(),
        memoriesStorage.save(),
      ])
      if (summariesStorage.error.value || memoriesStorage.error.value)
        throw new Error('摘要或记忆持久化失败，任务将重试')
      memoryCountResetForJob(job)
      countReset = true
      await messageCountMapStorage.save()
      if (messageCountMapStorage.error.value)
        throw new Error('记忆计数持久化失败，任务将重试')
      job.status = 'done'
      job.processingAt = undefined
      pruneCompletedDigestJobs()
      await digestJobsStorage.save()
      return true
    }
    catch (error) {
      if (countReset)
        messageCountMap.value[job.contactId] = messageCountBefore
      job.status = job.attempts >= 3 ? 'failed' : 'pending'
      job.nextAttemptAt = Date.now() + Math.min(15 * 60 * 1000, job.attempts * 60 * 1000)
      job.processingAt = undefined
      job.lastError = error instanceof Error ? error.message : String(error)
      await digestJobsStorage.save()
      console.warn('⚠️ 记忆整理任务失败，将在前台恢复后重试:', error)
      return false
    }
    finally {
      clearTimeout(timeoutId)
    }
  }

  function memoryCountResetForJob(job: MemoryDigestJob) {
    const id = job.contactId
    // 只消费任务创建时已累计的轮数；任务之后新产生的轮次保留。
    const current = messageCountMap.value[id] || 0
    messageCountMap.value[id] = Math.max(0, current - (job.turnCount || 0))
  }

  async function processPendingDigestJobs(model: string, provider: any, canProcess?: () => boolean): Promise<boolean> {
    if (digestProcessingPromise)
      return digestProcessingPromise
    digestProcessingPromise = processNextDigestJob(model, provider, canProcess)
      .finally(() => {
        digestProcessingPromise = null
      })
    return digestProcessingPromise
  }

  function shouldGenerateSummaryForContact(contactId = currentContactId.value): boolean {
    return !!contactId && (messageCountMap.value[contactId] || 0) >= config.value.summaryInterval
  }

  /** 将失败任务立即放回前台队列，供设置页手动重试。 */
  function retryDigestJobs(contactId = currentContactId.value): number {
    if (!contactId)
      return 0
    let count = 0
    const now = Date.now()
    for (const job of digestJobs.value) {
      if (job.contactId === contactId && job.status !== 'done' && (job.status !== 'processing' || isStaleProcessingJob(job, now))) {
        job.status = 'pending'
        job.nextAttemptAt = now
        job.processingAt = undefined
        job.lastError = undefined
        count++
      }
    }
    if (count > 0) {
      digestJobsStorage.notifyChange()
      void digestJobsStorage.save()
    }
    return count
  }

  /**
   * 添加摘要并维护最大数量限制
   */
  function addSummary(summary: ConversationSummary) {
    summaries.value.push(summary)

    // 清理过期摘要
    if (config.value.summaryRetentionDays > 0) {
      const expiryTime = Date.now() - (config.value.summaryRetentionDays * 24 * 60 * 60 * 1000)
      summaries.value = summaries.value.filter(s =>
        s.importance >= 5 || s.createdAt > expiryTime,
      )
    }

    // 维护最大数量（保留重要的）
    if (summaries.value.length > config.value.maxSummaries) {
      summaries.value = summaries.value
        .sort((a, b) => {
          // 重要性高的优先保留
          if (a.importance !== b.importance) {
            return b.importance - a.importance
          }
          // 时间新的优先保留
          return b.createdAt - a.createdAt
        })
        .slice(0, config.value.maxSummaries)
    }
  }

  // ============ 核心功能：创建记忆 ============

  /**
   * 创建新的记忆项
   */
  async function createMemory(
    content: string,
    keywords: string[] = [],
    isImportant: boolean = false,
    emotion?: string,
    metadata: {
      category?: MemoryCategory
      confidence?: number
      importance?: number
      source?: 'explicit' | 'auto_digest' | 'legacy'
      sourceJobId?: string
      contactId?: string
    } = {},
  ): Promise<MemoryItem | null> {
    // 🛡️ 拒绝创建「孤儿记忆」：写入瞬间没有激活联系人时，contactId 会落成 undefined，
    // 这类记忆永远不会被 retrieveRelevantMemories 召回（那里有 m.contactId 守卫），
    // 却会在启动时被 migrateUnassignedMemories 无差别过继给当前联系人，造成跨角色记忆泄漏。
    // 从源头拒绝，杜绝孤儿产生。调用方（chat-actions/chat-post-processors）均已忽略返回值。
    const ownerContactId = metadata.contactId || currentContactId.value
    if (!ownerContactId) {
      console.warn('[createMemory] 无激活联系人，跳过创建以避免产生孤儿记忆')
      return null
    }

    // 如果没有提供关键词，自动提取
    if (keywords.length === 0) {
      keywords = extractKeywords(content.toLowerCase()).slice(0, 5)
    }

    // 🔧 去重：防止重复记忆
    const contactMemoriesNow = memories.value.filter(m =>
      m.contactId === ownerContactId && (m.status || 'active') === 'active',
    )
    const { isDuplicateText } = await import('../utils/similarity')
    if (isDuplicateText(content, contactMemoriesNow.map(m => m.content))) {
      console.debug('[createMemory] 跳过重复记忆:', content.slice(0, 30))
      return null
    }

    const memory: MemoryItem = {
      id: `memory_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      content,
      keywords,
      isImportant,
      category: metadata.category || (metadata.source === 'auto_digest' ? 'fact' : 'fact'),
      status: 'active',
      confidence: metadata.confidence ?? (metadata.source === 'auto_digest' ? 0.75 : 1),
      lastSeenAt: Date.now(),
      source: metadata.source || 'explicit',
      sourceJobId: metadata.sourceJobId,
      importance: metadata.importance ?? (isImportant ? 5 : 1),
      timestamp: Date.now(),
      emotion,
      contactId: ownerContactId, // 关联当前联系人（此处已保证非空）
    }

    addMemory(memory)

    return memory
  }

  /**
   * 添加记忆并维护最大数量限制
   * 注意：只清理当前联系人的记忆，不影响其他联系人
   */
  function addMemory(memory: MemoryItem) {
    memories.value.push(memory)

    // 按联系人分组维护最大数量
    const contactId = memory.contactId
    if (contactId) {
      const contactMemories = memories.value.filter(m =>
        m.contactId === contactId && (m.status || 'active') === 'active',
      )

      if (contactMemories.length > config.value.maxMemories) {
        const important = contactMemories.filter(m => m.isImportant)
        const normal = contactMemories
          .filter(m => !m.isImportant)
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, config.value.maxMemories - important.length)

        // 保留其他联系人的记忆，以及当前联系人的历史状态记录。
        const otherContactsMemories = memories.value.filter(m => m.contactId !== contactId)
        const inactiveMemories = memories.value.filter(m =>
          m.contactId === contactId && (m.status || 'active') !== 'active',
        )
        memories.value = [...otherContactsMemories, ...inactiveMemories, ...important, ...normal]
      }
    }
  }

  /**
   * 更新记忆的重要性
   */
  function updateMemoryImportance(memoryId: string, isImportant: boolean) {
    const memory = memories.value.find(m => m.id === memoryId)
    if (memory) {
      memory.isImportant = isImportant
    }
  }

  /**
   * 删除记忆
   */
  function deleteMemory(memoryId: string) {
    const index = memories.value.findIndex(m => m.id === memoryId)
    if (index !== -1) {
      memories.value.splice(index, 1)
    }
  }

  /**
   * 删除摘要
   */
  function deleteSummary(summaryId: string) {
    const index = summaries.value.findIndex(s => s.id === summaryId)
    if (index !== -1) {
      summaries.value.splice(index, 1)
    }
  }

  // ============ 工具函数 ============

  /**
   * 格式化相对时间
   */
  function formatRelativeTime(timestamp: number): string {
    const now = Date.now()
    const diff = now - timestamp
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0)
      return `${days}天前`
    if (hours > 0)
      return `${hours}小时前`
    if (minutes > 0)
      return `${minutes}分钟前`
    return '刚刚'
  }

  /**
   * 清空所有记忆（慎用）
   */
  function clearAllMemories() {
    summaries.value = []
    memories.value = []
    digestJobs.value = []
    // 清空所有联系人的消息计数
    messageCountMap.value = {}
  }

  /**
   * 清空当前联系人的记忆
   */
  function clearCurrentContactMemories() {
    if (!currentContactId.value)
      return

    memories.value = memories.value.filter(m => m.contactId !== currentContactId.value)
    digestJobs.value = digestJobs.value.filter(j => j.contactId !== currentContactId.value)
    // 重置当前联系人的消息计数
    if (currentContactId.value) {
      messageCountMap.value[currentContactId.value] = 0
    }
  }

  /**
   * 清空当前联系人的摘要
   */
  function clearCurrentContactSummaries() {
    if (!currentContactId.value)
      return

    summaries.value = summaries.value.filter(s => s.contactId !== currentContactId.value)
    // 重置当前联系人的消息计数
    if (currentContactId.value) {
      messageCountMap.value[currentContactId.value] = 0
    }
  }

  /**
   * 🗑️ 删除指定联系人的所有记忆和摘要
   * @param contactId 联系人ID
   */
  function deleteContactMemories(contactId: string) {
    // 删除该联系人的所有记忆
    memories.value = memories.value.filter(m => m.contactId !== contactId)

    // 删除该联系人的所有摘要
    summaries.value = summaries.value.filter(s => s.contactId !== contactId)
    digestJobs.value = digestJobs.value.filter(j => j.contactId !== contactId)

    // 删除该联系人的消息计数
    delete messageCountMap.value[contactId]
  }

  /**
   * 清理某个联系人的所有相关引用（孤儿数据）
   */
  function purgeByContactId(contactId: string) {
    if (!contactId)
      return
    deleteContactMemories(contactId)
  }

  /**
   * 设置当前联系人ID
   * 用于切换联系人时更新记忆系统的上下文
   * 注意：不再重置消息计数，因为计数已按联系人持久化
   */
  function setCurrentContact(contactId: string | null) {
    currentContactId.value = contactId
  }

  /**
   * 增加已完成的正常对话轮数（按联系人持久化）。
   * 可显式传入联系人，避免异步回复期间切换联系人后计数写错会话。
   */
  function incrementMessageCount(contactId = currentContactId.value) {
    if (!contactId)
      return
    const id = contactId
    messageCountMap.value[id] = (messageCountMap.value[id] || 0) + 1
  }

  /**
   * 重置当前联系人的消息计数
   */
  function resetMessageCount() {
    if (!currentContactId.value)
      return
    messageCountMap.value[currentContactId.value] = 0
  }

  /**
   * 检查是否应该生成摘要
   */
  const shouldGenerateSummary = computed(() =>
    messageCountSinceLastSummary.value >= config.value.summaryInterval,
  )

  // ============ 导出 ============

  return {
    // 状态
    summaries,
    memories,
    config,
    currentContactId,
    currentContactMemories,
    currentContactSummaries,
    importantMemories,
    normalMemories,
    recentSummaries,
    currentContactDigestJobs,
    digestJobs,
    waitForReady,
    messageCountSinceLastSummary,
    shouldGenerateSummary,
    shouldGenerateSummaryForContact,

    // 方法
    retrieveRelevantMemories,
    generateSummary,
    generateDigest,
    enqueueDigestJob,
    processPendingDigestJobs,
    retryDigestJobs,
    recoverStaleDigestJobs,
    createMemory,
    updateMemoryImportance,
    deleteMemory,
    deleteSummary,
    formatRelativeTime,
    clearAllMemories,
    clearCurrentContactMemories,
    clearCurrentContactSummaries,
    deleteContactMemories,
    purgeByContactId,
    incrementMessageCount,
    resetMessageCount,
    setCurrentContact,
  }
})
